import React from 'react';
import { Container, Typography, Box, Paper, List, ListItem, ListItemText } from '@mui/material';

const DeleteAccount = () => {
  return (
    <Container maxWidth="md" sx={{ py: 8 }}>
      <Paper elevation={0} sx={{ p: 4, backgroundColor: '#f9f9f9' }}>
        <Typography variant="h3" component="h1" gutterBottom sx={{ mb: 1, fontWeight: 'bold' }}>
          Account & Data Deletion
        </Typography>

        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" sx={{ color: '#666', mb: 1 }}>
            <strong>App Name:</strong> Macronix Verify
          </Typography>
          <Typography variant="body2" sx={{ color: '#666' }}>
            <strong>Developer:</strong> Macronix
          </Typography>
        </Box>

        <Typography variant="body1" sx={{ mb: 4, lineHeight: 1.8 }}>
          This page explains how users of the <strong>Macronix Verify</strong> mobile application can 
          request deletion of their account and associated personal data.
        </Typography>

        <Typography variant="h5" component="h2" sx={{ mt: 4, mb: 2, fontWeight: 'bold' }}>
          How to Request Account Deletion
        </Typography>

        {/* Option 1 */}
        <Paper sx={{ p: 3, mb: 2, backgroundColor: '#f1f5f9', borderLeft: '4px solid #2563eb' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
            Option 1: Email Request
          </Typography>
          <List sx={{ pl: 2 }}>
            <ListItem sx={{ display: 'list-item' }}>
              <ListItemText 
                primary="Send an email to: support@macronixverify.com"
                primaryTypographyProps={{ variant: 'body2' }}
              />
            </ListItem>
            <ListItem sx={{ display: 'list-item' }}>
              <ListItemText 
                primary="Subject: Account Deletion Request"
                primaryTypographyProps={{ variant: 'body2' }}
              />
            </ListItem>
            <ListItem sx={{ display: 'list-item' }}>
              <ListItemText 
                primary="Please include:"
                primaryTypographyProps={{ variant: 'body2', fontWeight: 'bold' }}
              />
              <List sx={{ pl: 2, mt: 1 }}>
                <ListItem sx={{ display: 'list-item', py: 0 }}>
                  <ListItemText 
                    primary="Registered email address or phone number"
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
                <ListItem sx={{ display: 'list-item', py: 0 }}>
                  <ListItemText 
                    primary="User role (Candidate / Field Officer / Admin)"
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
              </List>
            </ListItem>
          </List>
        </Paper>

        {/* Option 2 */}
        <Paper sx={{ p: 3, mb: 3, backgroundColor: '#f1f5f9', borderLeft: '4px solid #2563eb' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
            Option 2: In-App Request
          </Typography>
          <List sx={{ pl: 2 }}>
            <ListItem sx={{ display: 'list-item' }}>
              <ListItemText 
                primary="Log in to the Macronix Verify app"
                primaryTypographyProps={{ variant: 'body2' }}
              />
            </ListItem>
            <ListItem sx={{ display: 'list-item' }}>
              <ListItemText 
                primary="Go to Profile → Request Account Deletion"
                primaryTypographyProps={{ variant: 'body2' }}
              />
            </ListItem>
            <ListItem sx={{ display: 'list-item' }}>
              <ListItemText 
                primary="Submit the deletion request"
                primaryTypographyProps={{ variant: 'body2' }}
              />
            </ListItem>
          </List>
        </Paper>

        <Typography variant="h5" component="h2" sx={{ mt: 4, mb: 2, fontWeight: 'bold' }}>
          Data That Will Be Deleted
        </Typography>

        <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
          After verifying the request, the following data associated with the user account will be 
          permanently deleted:
        </Typography>

        <List sx={{ pl: 2, mb: 3 }}>
          <ListItem sx={{ display: 'list-item' }}>
            <ListItemText 
              primary="User profile information (name, phone number, email address)"
              primaryTypographyProps={{ variant: 'body2' }}
            />
          </ListItem>
          <ListItem sx={{ display: 'list-item' }}>
            <ListItemText 
              primary="Authentication and access records"
              primaryTypographyProps={{ variant: 'body2' }}
            />
          </ListItem>
          <ListItem sx={{ display: 'list-item' }}>
            <ListItemText 
              primary="Uploaded photos and documents"
              primaryTypographyProps={{ variant: 'body2' }}
            />
          </ListItem>
          <ListItem sx={{ display: 'list-item' }}>
            <ListItemText 
              primary="Location (GPS) data collected during verification"
              primaryTypographyProps={{ variant: 'body2' }}
            />
          </ListItem>
          <ListItem sx={{ display: 'list-item' }}>
            <ListItemText 
              primary="Verification records linked to the user account"
              primaryTypographyProps={{ variant: 'body2' }}
            />
          </ListItem>
        </List>

        <Typography variant="h5" component="h2" sx={{ mt: 4, mb: 2, fontWeight: 'bold' }}>
          Data Retention
        </Typography>

        <Typography variant="body1" sx={{ mb: 4, lineHeight: 1.8 }}>
          Certain information may be retained for legal, regulatory, audit, or compliance purposes 
          for a maximum period of <strong>90 days</strong>. Retained data is securely stored and not 
          used for any other purpose.
        </Typography>

        <Typography variant="h5" component="h2" sx={{ mt: 4, mb: 2, fontWeight: 'bold' }}>
          Processing Time
        </Typography>

        <Typography variant="body1" sx={{ mb: 4, lineHeight: 1.8 }}>
          Account and data deletion requests are processed within <strong>7–14 business days</strong> 
          after successful verification of the request.
        </Typography>

        <Typography variant="h5" component="h2" sx={{ mt: 4, mb: 2, fontWeight: 'bold' }}>
          Contact
        </Typography>

        <Typography variant="body1" sx={{ mb: 4, lineHeight: 1.8 }}>
          If you have any questions regarding account or data deletion, please contact:
          <br />
          <strong>Email:</strong> support@macronixverify.com
        </Typography>

        <Box sx={{ mt: 6, pt: 4, borderTop: '1px solid #e0e0e0', backgroundColor: '#f0f0f0', p: 3, borderRadius: 1 }}>
          <Typography variant="caption" sx={{ color: '#666' }}>
            Last updated: January 2025
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
};

export default DeleteAccount;
